import React, { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import EmptyState from "../components/EmptyState";
import { Buildings, PencilSimple, Trash, Check, X, Plus, Warning, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Customers() {
  const { hasRole, loadCustomers, currentCustomer, setCurrentCustomer } = useAuth();
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", contact_number: "", email: "", report_recipients: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", report_recipients: "" });
  const admin = hasRole("admin");

  const load = async () => {
    const { data } = await api.get("/customers");
    setRows(data);
    loadCustomers();
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setForm({ name: "", contact_person: "", contact_number: "", email: "", report_recipients: "" });
      setAdding(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add customer"); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/customers/${id}`, { name: editForm.name, report_recipients: editForm.report_recipients });
      toast.success("Updated");
      if (currentCustomer?.id === id) setCurrentCustomer({ ...currentCustomer, name: editForm.name });
      setEditId(null);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Send today's daily report now for ${currentCustomer?.name || "this customer"}?`)) return;
    try {
      const { data } = await api.post("/reports/send-daily-now");
      if (data.sent_to?.length) toast.success(`Report sent to ${data.sent_to.length} recipient(s): ${data.sent_to.join(", ")}${data.failed?.length ? ` (failed: ${data.failed.join(", ")})` : ""}`, { duration: 8000 });
      else toast.error(`Report not delivered${data.failed?.length ? ` — failed for: ${data.failed.join(", ")}` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send report"); }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Customers</h1>
          <p className="mt-1 text-sm text-zinc-500">Each customer's sites, dumpers and tyres are kept fully separate.</p>
        </div>
        {admin && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={sendNow} data-testid="send-daily-now-btn"
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
              <PaperPlaneTilt size={16} weight="bold" className="mr-1" /> Send today's report
            </Button>
            {!adding && (
              <Button onClick={() => setAdding(true)} data-testid="add-customer-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
                <Plus size={16} weight="bold" className="mr-1" /> Add Customer
              </Button>
            )}
          </div>
        )}
      </div>

      {adding && (
        <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5 ring-1 ring-white/5" data-testid="add-customer-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input data-testid="cust-name" placeholder="Customer name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950" />
            <Input placeholder="Contact person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="bg-zinc-950" />
            <Input placeholder="Contact number" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="bg-zinc-950" />
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-950" />
            <Input data-testid="cust-report-recipients" placeholder="Daily report emails — comma separated (e.g. ops@acme.com, boss@acme.com)" value={form.report_recipients} onChange={(e) => setForm({ ...form, report_recipients: e.target.value })} className="bg-zinc-950 sm:col-span-2" />
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={create} data-testid="save-customer-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState icon={Buildings} title="No customers yet" hint={admin ? "Add your first customer to get started." : "Ask your admin to add a customer."} />
      ) : (
        <div className="overflow-x-auto rounded-sm border border-zinc-800 ring-1 ring-white/5">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-zinc-950 text-left text-zinc-500">
              <tr>
                <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Daily Report Emails</th>
                <th className="px-4 py-3">Mine Sites</th>{admin && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rows.map((c) => (
                <tr key={c.id} className="bg-zinc-900" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    {editId === c.id ? (
                      <Input data-testid={`edit-name-${c.id}`} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-8 bg-zinc-950" />
                    ) : (
                      <span className="font-display font-bold text-zinc-50">{c.name}{currentCustomer?.id === c.id && <span className="ml-2 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">ACTIVE</span>}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{c.contact_person || "—"}{c.contact_number ? ` · ${c.contact_number}` : ""}</td>
                  <td className="px-4 py-3 text-zinc-400">
                    {editId === c.id ? (
                      <Input data-testid={`edit-recipients-${c.id}`} value={editForm.report_recipients} onChange={(e) => setEditForm({ ...editForm, report_recipients: e.target.value })} placeholder="comma separated" className="h-8 bg-zinc-950" />
                    ) : (
                      <span className="text-xs">{(c.report_recipients || []).join(", ") || "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-zinc-300">{c.site_count ?? 0}</td>
                  {admin && (
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {editId === c.id ? (
                          <>
                            <Button size="icon" variant="ghost" data-testid={`save-edit-${c.id}`} onClick={() => saveEdit(c.id)}><Check size={16} className="text-emerald-400" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => setEditId(null)}><X size={16} /></Button>
                          </>
                        ) : (
                          <>
                            <Button size="icon" variant="ghost" data-testid={`edit-customer-${c.id}`} onClick={() => { setEditId(c.id); setEditForm({ name: c.name, report_recipients: (c.report_recipients || []).join(", ") }); }}><PencilSimple size={16} /></Button>
                            <Button size="icon" variant="ghost" data-testid={`delete-customer-${c.id}`} onClick={() => remove(c)}><Trash size={16} className="text-rose-400" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.some((c) => (c.site_count ?? 0) > 0) && (
        <p className="flex items-center gap-2 text-xs text-zinc-500"><Warning size={14} className="text-amber-500" /> A customer must have no mine sites before it can be deleted.</p>
      )}
    </div>
  );
}
